import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID, inject } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { StyleManagerService } from './style-manager.service';

export type AppTheme = 'dark' | 'light';

const STORAGE_KEY = 'cimalti-theme';
const DEFAULT_THEME: AppTheme = 'light';

/** Global theme switcher.
 *  Persists the preference in localStorage and exposes the active theme
 *  as `data-theme` on <body> so every CSS rule that keys off
 *  `body[data-theme="light"]` (the dark/light token blocks in
 *  `_design-tokens.scss` and `material.theme.scss`) flips at runtime.
 *
 *  The legacy `setTheme(name, isDark)` API is kept as a thin wrapper
 *  so existing call-sites (`pages/home/nav/nav.component.ts`) keep
 *  working — they now drive the same `data-theme` switch instead of
 *  loading an extra `material-dark.theme.css` stylesheet. */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  private http = inject(HttpClient);
  private styleManager = inject(StyleManagerService);

  private readonly isBrowser: boolean;
  private theme$ = new BehaviorSubject<AppTheme>(DEFAULT_THEME);
  readonly current$ = this.theme$.asObservable();

  constructor(
    @Inject(DOCUMENT) private doc: Document,
    @Inject(PLATFORM_ID) platformId: object,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
    const initial = this.readStored() ?? DEFAULT_THEME;
    this.theme$.next(initial);
    this.applyToBody(initial);
  }

  get current(): AppTheme {
    return this.theme$.value;
  }

  set(theme: AppTheme): void {
    if (theme === this.theme$.value) return;
    this.theme$.next(theme);
    this.applyToBody(theme);
    this.persist(theme);
  }

  toggle(): void {
    this.set(this.current === 'dark' ? 'light' : 'dark');
  }

  /** Legacy helper kept for backwards-compat with the old
   *  `setTheme('material-dark', isDark)` toggle pattern. The theme name
   *  is ignored — only the boolean flips between dark / light. */
  setTheme(_themeToSet: string, isDark: boolean): void {
    this.set(isDark ? 'dark' : 'light');
  }

  /** Legacy: returns options.json for the now-defunct theme picker. */
  getThemeOptions(): Observable<Array<any>> {
    return this.http.get<Array<any>>('assets/options.json');
  }

  private readStored(): AppTheme | null {
    if (!this.isBrowser) return null;
    try {
      const v = localStorage.getItem(STORAGE_KEY);
      if (v === 'light' || v === 'dark') return v;
    } catch { /* noop */ }
    return null;
  }

  private persist(theme: AppTheme): void {
    if (!this.isBrowser) return;
    try { localStorage.setItem(STORAGE_KEY, theme); } catch { /* noop */ }
  }

  private applyToBody(theme: AppTheme): void {
    const body = this.doc?.body;
    if (!body) return;
    body.setAttribute('data-theme', theme);
    this.doc.documentElement.setAttribute('data-theme', theme);
  }
}
